import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useRef } from 'react';

import { FormProvider, RHFAutocomplete, RHFTextField } from 'src/components/hook-form';
import RHFChipSelect from 'src/components/hook-form/rhf-chip-select';
import RhfDatePicker from 'src/components/hook-form/rhf-date-picker';
import Iconify from 'src/components/iconify';

import { DiscountType, HOW_TO_USE_STEP_MAX_LENGTH, useTableCollapse } from './benefit-form.hook';

// ----------------------------------------------------------------------

interface BenefitFormProps {
  row: any;
  getData: () => void;
  onClose?: () => void;
}

export default function BenefitForm({ row, getData, onClose }: BenefitFormProps) {
  const {
    methods,
    onSubmit,
    errors,
    handleButtonClick,
    handleBannerChange,
    handleThumbnailChange,
    bannerInputRef,
    thumbnailInputRef,
    isSubmitting,
    isLoading,
    values,
    categoriesOptions,
    levelsOptions,
    storesOptions,
    restaurantsOptions,
    typeValue,
    handleLevelToggle,
    selectedLevels,
    howToUseSteps,
    handleAddHowToUseStep,
    handleRemoveHowToUseStep,
    handleHowToUseStepChange,
    selectedStoreOption,
    handleStoreOptionChange,
    handleDownloadExampleSheet,
    selectedVoucherOption,
    selectedCodeVoucherOption,
    setSelectedVoucherOption,
    handleVoucherCodeTypeToggle,
    handleDownloadVoucherCodes,
    isEditMode,
    handleCsvFileChange,
    csvFile,
    isStockDisabled,
    isUniqueCodeOptionDisabled,
    previousCsvFileName,
    voucherStats,
    promotionOptions,
  } = useTableCollapse(row, getData, onClose);

  const csvInputRef = useRef<HTMLInputElement>(null);

  const handleCsvClick = () => {
    csvInputRef.current?.click();
  };

  const shouldShowVouchersToAddField = isEditMode && (selectedCodeVoucherOption === 'import' || selectedCodeVoucherOption === 'generate');

  const getStockPlaceholder = () => {
    if (selectedCodeVoucherOption === 'uniqueCode') {
      return 'Ilimitado';
    }
    if (isEditMode) {
      return 'Estoque atual';
    }
    return 'Quantidade de Vouchers';
  };

  const levelsAndDescriptionFields = (
    <>
      <Stack spacing={1}>
        <Typography variant="body2" fontWeight={600}>
          Nível do Benefício
        </Typography>
        <Stack direction="row" spacing={1}>
          {levelsOptions.map((level) => (
            <Chip
              sx={{ borderRadius: 100 }}
              key={level.value}
              label={level.label}
              color="primary"
              variant={selectedLevels?.includes(level.value) ? 'filled' : 'outlined'}
              onClick={() => handleLevelToggle(level.value)}
            />
          ))}
        </Stack>
        {errors.levels && (
          <Typography variant="caption" color="error">
            {errors.levels.message}
          </Typography>
        )}
      </Stack>

      {typeValue === 'voucher' && selectedVoucherOption === 'custom' && (
        <Stack>
          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Título (fonte menor)
            </Typography>
            <RHFTextField
              name="discountTitle"
              variant="standard"
              placeholder="Ex.: Preço especial"
              inputProps={{ maxLength: 15 }}
              required
            />
            <Typography variant="caption" color="greyScale.300" sx={{ textAlign: 'right' }}>
              {values.discountTitle?.length || 0}/15
            </Typography>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Sub-título (fonte maior)
            </Typography>
            <RHFTextField name="discountSubtitle" variant="standard" placeholder="Ex.: R$8,00" inputProps={{ maxLength: 8 }} required />
            <Typography variant="caption" color="greyScale.300" sx={{ textAlign: 'right' }}>
              {values.discountSubtitle?.length || 0}/8
            </Typography>
          </Stack>
        </Stack>
      )}

      <Stack spacing={1}>
        <Typography variant="body2" fontWeight={600}>
          Descrição breve do benefício
        </Typography>

        <RHFTextField name="description" variant="standard" placeholder="Descrição breve do benefício" inputProps={{ maxLength: 55 }} />
        <Typography typography="body-sm" color="greyScale.300">
          Até 55 caracteres
        </Typography>
      </Stack>
    </>
  );

  // console.log(row);
  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <RHFTextField name="storeEvent" sx={{ display: 'none' }} />

      <Box sx={{ p: 3, backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <Stack direction="row" spacing={6} divider={<Divider orientation="vertical" flexItem />}>
          <Box sx={{ flex: 2, pr: 3 }}>
            <Stack spacing={3}>
              {row?.id && row.id !== '-' && row.type === typeValue && (typeValue === 'voucher' || typeValue === 'parking') && (
                <Stack alignItems="stretch" mb={3}>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'primary.main',
                      px: 1,
                      py: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography typography="bold-bd-md">Quantidade de Vouchers</Typography>
                      <Typography color="primary.main" typography="bold-bd-md">
                        {voucherStats.total.toLocaleString('pt-BR')}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography typography="bold-bd-md">Vouchers Resgatados</Typography>
                      <Typography color="primary.main" typography="bold-bd-md">
                        {voucherStats.redeemed.toLocaleString('pt-BR')}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography typography="bold-bd-md">Vouchers Usados</Typography>
                      <Typography color="primary.main" typography="bold-bd-md">
                        {voucherStats.used.toLocaleString('pt-BR')}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              )}
              <Stack spacing={1}>
                <Typography variant="body2" fontWeight={600}>
                  Nome do Benefício
                </Typography>
                <RHFTextField
                  variant="standard"
                  name="name"
                  placeholder="Nome do benefício"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body2" fontWeight={600}>
                  Categoria do Benefício
                </Typography>
                <RHFChipSelect name="category" variant="standard" options={categoriesOptions} placeholder="Categoria" multiple={false} />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body2" fontWeight={600}>
                  Tipo do Benefício
                </Typography>
                <RadioGroup
                  row
                  value={typeValue}
                  onChange={(e) => methods.setValue('type', e.target.value as 'informative' | 'voucher' | 'parking')}
                >
                  <FormControlLabel value="informative" control={<Radio size="small" />} label="Informativo" />
                  <FormControlLabel value="voucher" control={<Radio size="small" />} label="Voucher" />
                  <FormControlLabel value="parking" control={<Radio size="small" />} label="Estacionamento" />
                </RadioGroup>
                {errors.type && (
                  <Typography variant="caption" color="error">
                    {errors.type.message}
                  </Typography>
                )}
              </Stack>

              {typeValue !== 'parking' && (
                <Stack spacing={1}>
                  <Typography variant="body2" fontWeight={600}>
                    Loja / Restaurante / Evento
                  </Typography>
                  <RadioGroup
                    row
                    value={selectedStoreOption}
                    onChange={(e) => {
                      const option = e.target.value;
                      if (option === 'select') {
                        handleStoreOptionChange('store');
                      } else if (option === 'restaurant') {
                        handleStoreOptionChange('restaurant');
                      } else if (option === 'manual') {
                        handleStoreOptionChange('event');
                      }
                    }}
                  >
                    <FormControlLabel value="select" control={<Radio size="small" />} label="Selecionar Loja" />
                    <FormControlLabel value="restaurant" control={<Radio size="small" />} label="Selecionar Restaurante" />
                    <FormControlLabel value="manual" control={<Radio size="small" />} label="Evento Manual" />
                  </RadioGroup>
                  {errors.storeEvent && (
                    <Typography variant="caption" color="error">
                      {errors.storeEvent.message}
                    </Typography>
                  )}

                  {selectedStoreOption === 'select' && (
                    <>
                      <RHFAutocomplete
                        options={storesOptions}
                        name="store"
                        placeholder="Loja"
                        variant="standard"
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') {
                            return storesOptions.find((opt) => opt.value === option)?.label || option;
                          }
                          return (option as any)?.label || '';
                        }}
                      />
                      {errors.store && (
                        <Typography variant="caption" color="error">
                          {errors.store.message}
                        </Typography>
                      )}
                    </>
                  )}

                  {selectedStoreOption === 'restaurant' && (
                    <>
                      <RHFAutocomplete
                        options={restaurantsOptions}
                        name="restaurant"
                        placeholder="Restaurante"
                        variant="standard"
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') {
                            return restaurantsOptions.find((opt) => opt.value === option)?.label || option;
                          }
                          return (option as any)?.label || '';
                        }}
                      />
                      {errors.restaurant && (
                        <Typography variant="caption" color="error">
                          {errors.restaurant.message}
                        </Typography>
                      )}
                    </>
                  )}

                  {selectedStoreOption === 'manual' && (
                    <Stack direction="row" spacing={4} alignItems="center">
                      <Stack direction="column">
                        <Typography variant="body2" fontWeight={600}>
                          Nome do Evento
                        </Typography>
                        <RHFTextField
                          name="event"
                          placeholder="Preencha o nome do evento"
                          variant="standard"
                          size="medium"
                          sx={{
                            width: 300,
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                            },
                          }}
                        />
                      </Stack>
                      <Grid item xs={12} md="auto" alignSelf="stretch">
                        <Divider orientation="vertical" />
                      </Grid>
                      <Stack direction="column" spacing={1}>
                        <Typography typography="bold-bd-md">Thumbnail</Typography>
                        <Typography typography="body-sm" color="greyScale.300">
                          Proporção no APP: 136px x 136px
                        </Typography>
                        <Typography typography="body-sm" color="greyScale.300">
                          Resolução sugerida: 408px x 408px
                        </Typography>
                        {values.thumb?.url ? (
                          <Box
                            height={136}
                            width={136}
                            component="img"
                            src={
                              values.thumb.url.startsWith('http') || values.thumb.url.startsWith('data:')
                                ? values.thumb.url
                                : `data:${values.thumb.type || 'image/png'};base64,${values.thumb.url}`
                            }
                            sx={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <Iconify icon="ri:image-line" height={60} width={60} color="greyScale.300" />
                        )}
                        <Stack
                          color="greyScale.300"
                          direction="row"
                          alignItems="center"
                          justifyContent="start"
                          spacing={1}
                          onClick={() => handleButtonClick('thumb')}
                          sx={{ ':hover': { cursor: 'pointer' } }}
                        >
                          <Typography typography="body-sm">Escolher Arquivo</Typography>
                          <Iconify icon="ri:download-2-line" />
                        </Stack>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              )}
              <Stack spacing={1}>
                <Typography variant="body2" fontWeight={600}>
                  Prazo
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <RhfDatePicker name="startDate" dateValueFormat="L" popperZIndex={9999} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <RhfDatePicker name="endDate" dateValueFormat="L" popperZIndex={9999} />
                  </Box>
                </Stack>
              </Stack>
              {typeValue === 'voucher' && (
                <>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Stack spacing={1} sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Estoque
                      </Typography>
                      {selectedCodeVoucherOption === 'uniqueCode' ? (
                        <Box
                          sx={{
                            padding: '8px 0',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            color: 'greyScale.300',
                          }}
                        >
                          Ilimitado
                        </Box>
                      ) : (
                        <RHFTextField
                          name="stock"
                          placeholder={getStockPlaceholder()}
                          variant="standard"
                          type="number"
                          inputProps={{ min: 0, step: '0' }}
                          disabled={isEditMode || isStockDisabled}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                            },
                            '& .Mui-disabled': {
                              color: 'greyScale.300',
                            },
                          }}
                        />
                      )}
                    </Stack>

                    {shouldShowVouchersToAddField && (
                      <Stack direction="row" spacing={1} sx={{ flex: 1 }} alignItems="flex-end">
                        <Typography variant="body2" sx={{ mb: '3.5px' }} color="greyScale.200">
                          +
                        </Typography>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                          <RHFTextField
                            name="vouchersQuantity"
                            placeholder="Quantidade de Vouchers"
                            variant="standard"
                            type="number"
                            inputProps={{ min: 0, step: '0' }}
                            disabled={selectedCodeVoucherOption === 'import'}
                            FormHelperTextProps={{ sx: { position: 'absolute', bottom: -18, whiteSpace: 'nowrap' } }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                              },
                              '& .Mui-disabled': {
                                color: 'greyScale.300',
                              },
                            }}
                          />
                        </Stack>
                      </Stack>
                    )}
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={600}>
                      Desconto
                    </Typography>
                    <RadioGroup
                      row
                      value={selectedVoucherOption}
                      onChange={(e) => {
                        const val = e.target.value as DiscountType | '';
                        setSelectedVoucherOption(val);
                        if (val !== '') {
                          methods.setValue('discountType', val);
                          if (val === 'courtesy' || val === 'buyAndGet' || val === 'custom') methods.setValue('discount', 0);
                          if (val !== 'custom') {
                            methods.setValue('discountTitle', '');
                            methods.setValue('discountSubtitle', '');
                          }
                        }
                      }}
                    >
                      <FormControlLabel value="percentage" control={<Radio size="small" />} label="Porcentagem" />
                      <FormControlLabel value="value" control={<Radio size="small" />} label="Valor Fixo" />
                      <FormControlLabel value="courtesy" control={<Radio size="small" />} label="Cortesia" />
                      <FormControlLabel value="buyAndGet" control={<Radio size="small" />} label="Compre e Ganhe" />
                      <FormControlLabel value="custom" control={<Radio size="small" />} label="Personalizado" />
                    </RadioGroup>

                    {(selectedVoucherOption === 'percentage' || selectedVoucherOption === 'value') && (
                      <RHFTextField
                        variant="standard"
                        name="discount"
                        placeholder={
                          selectedVoucherOption === 'percentage' ? 'Porcentagem em desconto' : 'Valor Fixo em desconto (em reais)'
                        }
                        size="small"
                        type="number"
                        inputProps={{ min: 0, max: selectedVoucherOption === 'percentage' ? 100 : undefined, step: '0.01' }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                          },
                        }}
                      />
                    )}
                  </Stack>

                  {levelsAndDescriptionFields}

                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={600}>
                      Código do Voucher
                    </Typography>
                    <Typography variant="body2" color="greyScale.400" sx={{ fontSize: '12px' }}>
                      Só vai ser possível gerar lista de códigos com estoque finito.
                    </Typography>
                    <RadioGroup
                      row
                      value={selectedCodeVoucherOption}
                      onChange={({ target }) => {
                        const { value } = target as { value: 'import' | 'generate' | 'uniqueCode' };
                        handleVoucherCodeTypeToggle(value);
                      }}
                    >
                      <FormControlLabel value="import" control={<Radio size="small" />} label="Importar lista de Código" />
                      <FormControlLabel value="generate" control={<Radio size="small" />} label="Gerar Lista de Código" />
                      <FormControlLabel
                        value="uniqueCode"
                        control={<Radio size="small" />}
                        label="Cadastrar Código Único"
                        disabled={isUniqueCodeOptionDisabled}
                      />
                    </RadioGroup>
                    {selectedCodeVoucherOption === 'import' && (
                      <Stack spacing={1}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <IconButton size="small" color="primary" title="Baixar códigos atuais" onClick={handleDownloadVoucherCodes}>
                              <Iconify icon="ri:download-2-line" width={18} height={18} />
                            </IconButton>
                            <Iconify icon="ri:file-excel-2-line" height={60} width={60} color="greyScale.300" />
                            <Typography typography="body-sm">
                              {previousCsvFileName || `${(values.name || 'beneficio').trim().replace(/\s+/g, '_')}_codigos`}
                            </Typography>
                          </Stack>

                          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

                          <Stack direction="row" spacing={1} alignItems="center">
                            <Iconify icon="ri:file-excel-2-line" height={60} width={60} color="greyScale.300" />
                            <Stack
                              color="greyScale.300"
                              direction="column"
                              alignItems="flex-start"
                              justifyContent="center"
                              spacing={1}
                              onClick={handleCsvClick}
                              sx={{ ':hover': { cursor: 'pointer' } }}
                            >
                              <Typography typography="body-sm">{csvFile?.name || 'Arquivo .CSV'}</Typography>
                              <Stack direction="row" spacing={1}>
                                <Typography typography="body-sm">Escolher Arquivo</Typography>
                                <Iconify icon="ri:download-2-line" />
                              </Stack>
                            </Stack>
                          </Stack>

                          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2 }}>
                            <Iconify icon="ri:information-line" width={20} height={20} color="info.main" sx={{ mt: 2, flexShrink: 0 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', maxWidth: 280 }}>
                              Será possível apenas adicionar novos códigos. Os códigos adicionados anteriormente não serão alterados.
                            </Typography>
                          </Stack>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={handleDownloadExampleSheet}
                            sx={{ textTransform: 'none' }}
                          >
                            <Iconify icon="ri:download-2-line" color="primary.main" sx={{ mr: 1, height: 20, width: 20 }} />
                            Planilha Padrão
                          </Button>
                        </Stack>
                      </Stack>
                    )}

                    {selectedCodeVoucherOption === 'generate' && isEditMode && (
                      <Box sx={{ width: '200px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <LoadingButton
                          loading={isSubmitting}
                          onClick={handleDownloadVoucherCodes}
                          variant="contained"
                          color="primary"
                          fullWidth
                        >
                          Baixar Lista Atual
                        </LoadingButton>
                      </Box>
                    )}

                    {selectedCodeVoucherOption === 'generate' &&
                      (!isEditMode || !values.vouchersQuantity || Number(values.vouchersQuantity) === 0) && (
                        <Box sx={{ width: '600px', display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary" align="center">
                            {!isEditMode
                              ? 'Salve o benefício primeiro para gerar a lista de códigos'
                              : 'Defina a quantidade de vouchers no campo "Quantidade de Vouchers" para gerar a lista'}
                          </Typography>
                        </Box>
                      )}

                    {selectedCodeVoucherOption === 'uniqueCode' && (
                      <Stack spacing={1}>
                        <RHFTextField
                          variant="standard"
                          name="voucherCode"
                          placeholder="Código do Voucher"
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'white',
                            },
                          }}
                        />
                      </Stack>
                    )}
                  </Stack>
                </>
              )}
              {typeValue === 'parking' && (
                <>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={600}>
                      Código promocional
                    </Typography>
                    <RHFAutocomplete
                      options={promotionOptions}
                      variant="standard"
                      name="parkingPromoInfo"
                      placeholder="Código fornecido pela WPS"
                    />
                  </Stack>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={600}>
                      Estoque total
                    </Typography>
                    <RHFTextField
                      name="vouchersQuantity"
                      placeholder="Quantidade do benefício total"
                      variant="standard"
                      type="number"
                      inputProps={{ min: 0, step: '0' }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                        },
                      }}
                    />
                  </Stack>
                </>
              )}

              {(typeValue === 'voucher' || typeValue === 'parking') && (
                <Stack spacing={1}>
                  <Typography variant="body2" fontWeight={600}>
                    Benefícios por usuário
                  </Typography>
                  <RHFTextField
                    name="redemptionLimitPerUser"
                    placeholder="Quantidade de quantas vezes o usuário poderá resgatar o benefício"
                    variant="standard"
                    type="number"
                    inputProps={{ min: 1, step: '1' }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                      },
                    }}
                  />
                </Stack>
              )}

              {typeValue !== 'voucher' && levelsAndDescriptionFields}

              <Stack spacing={1}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Typography typography="body-lg">
                    Como utilizar
                    <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                  </Typography>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    onClick={handleAddHowToUseStep}
                    sx={{ textTransform: 'none', fontSize: '12px', textDecoration: 'underline', minWidth: 0, p: 0 }}
                  >
                    Adicionar novo passo
                  </Button>
                </Stack>

                {howToUseSteps.map((step, index) => (
                  <Stack key={index} direction="row" spacing={2} alignItems="flex-start">
                    <Typography sx={{ minWidth: 24, flexShrink: 0, fontSize: 28, lineHeight: '36px', color: 'greyScale.300' }}>
                      {index + 1}.
                    </Typography>
                    <TextField
                      fullWidth
                      variant="standard"
                      placeholder="Passos para resgate"
                      value={step}
                      onChange={(e) => handleHowToUseStepChange(index, e.target.value)}
                      size="small"
                      inputProps={{ maxLength: HOW_TO_USE_STEP_MAX_LENGTH }}
                      error={step.length > HOW_TO_USE_STEP_MAX_LENGTH}
                      helperText={
                        step.length > HOW_TO_USE_STEP_MAX_LENGTH
                          ? `${step.length}/${HOW_TO_USE_STEP_MAX_LENGTH} - Cada passo deve ter no máximo ${HOW_TO_USE_STEP_MAX_LENGTH} caracteres`
                          : `${step.length}/${HOW_TO_USE_STEP_MAX_LENGTH}`
                      }
                      FormHelperTextProps={{ sx: { textAlign: 'right', mx: 0 } }}
                      sx={{ '& .MuiInputBase-input': { typography: 'body-lg' } }}
                    />
                    {howToUseSteps.length > 1 && (
                      <IconButton size="small" onClick={() => handleRemoveHowToUseStep(index)} color="error">
                        <Iconify icon="ri:delete-bin-line" width={24} />
                      </IconButton>
                    )}
                  </Stack>
                ))}
                {errors.howToUse && (
                  <Typography variant="caption" color="error">
                    {errors.howToUse.message}
                  </Typography>
                )}
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body2" fontWeight={600}>
                  Condições
                </Typography>
                <RHFTextField name="conditions" multiline rows={3} placeholder="Condições do Benefício" />
              </Stack>
            </Stack>
          </Box>
          <Box sx={{ width: '200px', px: 1.5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Stack spacing={1} sx={{ width: '100%' }}>
              <Typography typography="bold-bd-md">Banner</Typography>
              <Typography typography="body-sm" color="greyScale.300">
                Proporção no APP: 328px x 140px. Resolução sugerida: 984px x 420px
              </Typography>
              {values.banner.url ? (
                <Box
                  height={140}
                  width={328}
                  component="img"
                  src={
                    values.banner.url.startsWith('http') || values.banner.url.startsWith('data:')
                      ? values.banner.url
                      : `data:${values.banner.type || 'image/png'};base64,${values.banner.url}`
                  }
                  sx={{ objectFit: 'cover' }}
                />
              ) : (
                <Iconify icon="ri:image-line" height={60} width={60} color="greyScale.300" />
              )}
              <Stack
                color="greyScale.300"
                direction="row"
                alignItems="center"
                justifyContent="start"
                spacing={1}
                onClick={() => handleButtonClick('banner')}
                sx={{ ':hover': { cursor: 'pointer' } }}
              >
                <Typography typography="body-sm">Escolher Arquivo</Typography>
                <Iconify icon="ri:download-2-line" />
              </Stack>
              {errors.banner && (
                <Typography typography="body-sm" color="error">
                  Selecione um arquivo!
                </Typography>
              )}
            </Stack>
          </Box>

          <Box sx={{ width: '120px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <LoadingButton loading={isSubmitting || isLoading} type="submit" variant="contained" color="primary" fullWidth>
              Salvar
            </LoadingButton>
          </Box>
        </Stack>

        <input ref={bannerInputRef} type="file" accept="image/" style={{ display: 'none' }} onChange={handleBannerChange} />
        <input ref={thumbnailInputRef} type="file" accept="image/" style={{ display: 'none' }} onChange={handleThumbnailChange} />
        <input ref={csvInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCsvFileChange} />
      </Box>
    </FormProvider>
  );
}
